'use client';

import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { AlertCircle, HelpCircle, CheckCircle2, Info } from 'lucide-react';

type ModalIntent = 'success' | 'error' | 'info' | 'confirm' | 'danger';

interface ModalOptions {
  title?: string;
  message: string;
  intent: ModalIntent;
}

interface ModalContextType {
  showAlert: (message: string, title?: string, intent?: 'success' | 'error' | 'info') => Promise<void>;
  showConfirm: (message: string, title?: string, intent?: 'confirm' | 'danger') => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ModalOptions | null>(null);
  const [resolveFn, setResolveFn] = useState<((value: any) => void) | null>(null);

  const showAlert = useCallback((message: string, title?: string, intent: 'success' | 'error' | 'info' = 'info') => {
    return new Promise<void>((resolve) => {
      // Auto-detect intent from title if not provided explicitly
      let finalIntent = intent;
      if (title?.toLowerCase().includes('sukses')) finalIntent = 'success';
      if (title?.toLowerCase().includes('gagal') || title?.toLowerCase().includes('error')) finalIntent = 'error';
      
      setOptions({ message, title: title || 'Perhatian', intent: finalIntent });
      setResolveFn(() => resolve);
      setIsOpen(true);
    });
  }, []);

  const showConfirm = useCallback((message: string, title?: string, intent: 'confirm' | 'danger' = 'confirm') => {
    return new Promise<boolean>((resolve) => {
      setOptions({ message, title: title || 'Konfirmasi', intent });
      setResolveFn(() => resolve);
      setIsOpen(true);
    });
  }, []);

  const handleClose = (value: boolean | void) => {
    setIsOpen(false);
    if (resolveFn) {
      resolveFn(value);
    }
    setTimeout(() => {
      setOptions(null);
      setResolveFn(null);
    }, 300); // Wait for exit animation
  };

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}

      {/* Modal UI */}
      {isOpen && options && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-md animate-in fade-in duration-200"
            onClick={() => handleClose(false)} // Clicking outside returns false/void
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-sm bg-[#1c1c1c] rounded-[var(--radius)] p-6 shadow-2xl flex flex-col gap-4 animate-in zoom-in-95 duration-200 border-none">
            
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                options.intent === 'confirm' ? 'bg-blue-500/20 text-blue-500' : 
                options.intent === 'success' ? 'bg-green-500/20 text-green-500' :
                options.intent === 'error' || options.intent === 'danger' ? 'bg-red-500/20 text-red-500' :
                'bg-primary/20 text-primary'
              }`}>
                {options.intent === 'confirm' ? <HelpCircle size={24} /> : 
                 options.intent === 'success' ? <CheckCircle2 size={24} /> :
                 options.intent === 'error' || options.intent === 'danger' ? <AlertCircle size={24} /> :
                 <Info size={24} />}
              </div>
              <div className="pt-1">
                <h3 className="text-lg font-700 text-foreground tracking-tight">{options.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed whitespace-pre-wrap">
                  {options.message}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              {(options.intent === 'confirm' || options.intent === 'danger') && (
                <button
                  onClick={() => handleClose(false)}
                  className="px-4 py-1.5 rounded-[var(--radius)] bg-white/5 hover:bg-white/10 text-foreground text-sm font-600 transition-colors border-none"
                >
                  Batal
                </button>
              )}
              <button
                onClick={() => handleClose(options.intent === 'confirm' || options.intent === 'danger' ? true : undefined)}
                className={`px-4 py-1.5 rounded-[var(--radius)] text-sm font-600 transition-transform hover:scale-105 active:scale-95 shadow-lg border-none ${
                  options.intent === 'confirm' ? 'bg-blue-600 text-white shadow-blue-600/20' : 
                  options.intent === 'danger' ? 'bg-red-600 text-white shadow-red-600/20' :
                  'bg-primary text-primary-foreground shadow-primary/20'
                }`}
              >
                {options.intent === 'confirm' || options.intent === 'danger' ? 'Ya, Lanjutkan' : 'Mengerti'}
              </button>
            </div>

          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}
